import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Snackbar,
  Menu,
} from '@mui/material';
import {
  Refresh,
  PlayArrow,
  ListAlt,
  Schedule as ScheduleIcon,
  Timer,
  KeyboardArrowDown,
} from '@mui/icons-material';

// Base URL — same pattern as Planning.tsx (strip /api/v1 suffix)
const BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1'
).replace(/\/api\/v\d+$/, '');

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface WorkCenter {
  id?: string | number;
  code?: string;
  name: string;
}

interface Operation {
  segmentId?: string;
  operationId?: string;
  workOrderId?: string;
  productName?: string;
  scheduledStart?: string;
  scheduledEnd?: string;
  plannedDurationMinutes?: number;
  status?: string;
}

interface Schedule {
  id: string;
  name?: string;
}

interface SnackState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

interface WorkCentersResponse {
  data?: WorkCenter[];
}

interface DispatchListResponse {
  operations?: Operation[];
  data?: Operation[];
  workCenterId?: string;
}

interface SchedulesResponse {
  data?: Schedule[];
}

interface SummaryCardProps {
  label: string;
  value: string | number;
  color: string;
  icon: React.ReactNode;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

async function apiFetch(path: string, options: RequestInit = {}): Promise<unknown> {
  const token = localStorage.getItem('token');
  const resp = await fetch(`${BASE_URL}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...options,
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.json();
}

// Status chip colours
const statusChipProps = (status: string | undefined): { color: 'primary' | 'success' | 'default'; sx: Record<string, unknown> } => {
  switch ((status || '').toLowerCase()) {
    case 'scheduled':
      return { color: 'primary', sx: {} };
    case 'active':
      return { color: 'success', sx: {} };
    case 'completed':
      return { color: 'success', sx: { bgcolor: 'success.dark', color: '#fff' } };
    case 'pending':
    default:
      return { color: 'default', sx: {} };
  }
};

const formatTime = (isoString: string | undefined): string => {
  if (!isoString) return '--';
  try {
    return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return isoString;
  }
};

const todayIso = (): string => new Date().toISOString().slice(0, 10);

// ─── Summary cards ────────────────────────────────────────────────────────────
const SummaryCard: React.FC<SummaryCardProps> = ({ label, value, color, icon }) => (
  <Card>
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography color="textSecondary" variant="body2" gutterBottom>
            {label}
          </Typography>
          <Typography variant="h4" color={color}>
            {value}
          </Typography>
        </Box>
        <Box sx={{ color, fontSize: 40 }}>{icon}</Box>
      </Box>
    </CardContent>
  </Card>
);

// ─── Main DispatchList component ──────────────────────────────────────────────
const DispatchList: React.FC = () => {
  const [workCenters, setWorkCenters] = useState<WorkCenter[]>([]);
  const [selectedWorkCenter, setSelectedWorkCenter] = useState<string>('');
  const [date, setDate] = useState<string>(todayIso());
  const [operations, setOperations] = useState<Operation[]>([]);
  const [workCenterName, setWorkCenterName] = useState<string>('');
  const [loadingWC, setLoadingWC] = useState<boolean>(true);
  const [loadingOps, setLoadingOps] = useState<boolean>(false);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-schedule state
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [autoAnchorEl, setAutoAnchorEl] = useState<HTMLElement | null>(null);
  const [autoLoading, setAutoLoading] = useState<boolean>(false);

  // Snackbar
  const [snack, setSnack] = useState<SnackState>({ open: false, message: '', severity: 'success' });

  // ── Fetch work centres on mount ──
  const loadWorkCenters = useCallback(async () => {
    setLoadingWC(true);
    try {
      const data = await apiFetch('/api/v1/master-data/work-centers') as WorkCentersResponse | WorkCenter[];
      const list = (data as WorkCentersResponse).data ?? data;
      setWorkCenters(Array.isArray(list) ? list : []);
    } catch (e) {
      setSnack({ open: true, message: `Failed to load work centers: ${(e as Error).message}`, severity: 'error' });
    } finally {
      setLoadingWC(false);
    }
  }, []);

  useEffect(() => {
    loadWorkCenters();
  }, [loadWorkCenters]);

  // ── Fetch dispatch list ──
  const loadDispatchList = useCallback(async () => {
    if (!selectedWorkCenter || !date) {
      setSnack({ open: true, message: 'Please select a work center and date.', severity: 'warning' });
      return;
    }
    setLoadingOps(true);
    setError(null);
    setHasLoaded(false);
    try {
      const params = new URLSearchParams({ workCenterId: selectedWorkCenter, date });
      const data = await apiFetch(`/api/v1/scheduling/dispatch-list?${params.toString()}`) as DispatchListResponse;
      // Gateway envelope: { success, data: { workCenterId, date, operations: [...] } }
      const payload: any = (data as any)?.data ?? data;
      const ops: Operation[] = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.operations)
        ? payload.operations
        : Array.isArray((data as any)?.operations)
        ? (data as any).operations
        : [];
      const sorted = [...ops].sort(
        (a, b) => new Date(a.scheduledStart ?? '').getTime() - new Date(b.scheduledStart ?? '').getTime()
      );
      setOperations(sorted);
      // Resolve work center display name
      const wc = workCenters.find(
        (w) => String(w.id) === String(selectedWorkCenter) || w.code === selectedWorkCenter
      );
      setWorkCenterName(wc?.name ?? payload?.workCenterId ?? selectedWorkCenter);
    } catch (e) {
      setError((e as Error).message || 'Failed to load dispatch list');
      setOperations([]);
    } finally {
      setLoadingOps(false);
      setHasLoaded(true);
    }
  }, [selectedWorkCenter, date, workCenters]);

  // ── Fetch schedules for auto-schedule dropdown ──
  const loadSchedules = useCallback(async () => {
    try {
      const data = await apiFetch('/api/v1/scheduling/schedules') as SchedulesResponse | Schedule[];
      const list = (data as SchedulesResponse).data ?? data;
      setSchedules(Array.isArray(list) ? list : []);
    } catch (e) {
      setSnack({ open: true, message: `Failed to load schedules: ${(e as Error).message}`, severity: 'error' });
    }
  }, []);

  // ── Handle auto-schedule button click ──
  const handleAutoScheduleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    setAutoAnchorEl(e.currentTarget);
    await loadSchedules();
  };

  const handleAutoScheduleSelect = async (scheduleId: string) => {
    setAutoAnchorEl(null);
    setAutoLoading(true);
    try {
      await apiFetch(`/api/v1/scheduling/schedules/${scheduleId}/auto-schedule`, { method: 'POST' });
      setSnack({ open: true, message: 'Auto-scheduling triggered successfully.', severity: 'success' });
      // Reload dispatch list if we have a selection
      if (selectedWorkCenter && date) {
        await loadDispatchList();
      }
    } catch (e) {
      setSnack({ open: true, message: `Auto-schedule failed: ${(e as Error).message}`, severity: 'error' });
    } finally {
      setAutoLoading(false);
    }
  };

  // ── Derived summary metrics ──
  const totalOps = operations.length;
  const totalDuration = operations.reduce((sum, op) => sum + (op.plannedDurationMinutes ?? 0), 0);

  return (
    <Box>
      {/* ── Page header ── */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Operator Dispatch List
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadDispatchList}
          disabled={loadingOps || !selectedWorkCenter}
        >
          Refresh
        </Button>
      </Box>

      {/* ── Top bar: filters + actions ── */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          {/* Work centre selector */}
          <FormControl sx={{ minWidth: 220 }} size="small">
            <InputLabel>Work Center</InputLabel>
            <Select
              value={selectedWorkCenter}
              label="Work Center"
              onChange={(e) => setSelectedWorkCenter(e.target.value as string)}
              disabled={loadingWC}
              startAdornment={loadingWC ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
            >
              {workCenters.map((wc) => (
                <MenuItem key={wc.id ?? wc.code} value={String(wc.id ?? wc.code)}>
                  {wc.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Date picker */}
          <TextField
            label="Date"
            type="date"
            size="small"
            value={date}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />

          {/* Load button */}
          <Button
            variant="contained"
            onClick={loadDispatchList}
            disabled={loadingOps || !selectedWorkCenter || !date}
            startIcon={
              loadingOps ? <CircularProgress size={16} color="inherit" /> : <ListAlt />
            }
          >
            {loadingOps ? 'Loading...' : 'Load'}
          </Button>

          {/* Auto-Schedule button */}
          <Button
            variant="contained"
            color="success"
            endIcon={<KeyboardArrowDown />}
            onClick={handleAutoScheduleClick}
            disabled={autoLoading}
            startIcon={
              autoLoading ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />
            }
          >
            Auto-Schedule
          </Button>
          <Menu
            anchorEl={autoAnchorEl}
            open={Boolean(autoAnchorEl)}
            onClose={() => setAutoAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            {schedules.length === 0 ? (
              <MenuItem disabled>No active schedules found</MenuItem>
            ) : (
              schedules.map((s) => (
                <MenuItem
                  key={s.id}
                  onClick={() => handleAutoScheduleSelect(s.id)}
                >
                  {s.name ?? s.id}
                </MenuItem>
              ))
            )}
          </Menu>
        </Box>
      </Paper>

      {/* ── Summary cards (only when data loaded) ── */}
      {hasLoaded && (
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={4}>
            <SummaryCard
              label="Work Center"
              value={workCenterName || '--'}
              color="primary.main"
              icon={<ScheduleIcon fontSize="inherit" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <SummaryCard
              label="Total Operations"
              value={totalOps}
              color="info.main"
              icon={<ListAlt fontSize="inherit" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <SummaryCard
              label="Total Planned Duration (min)"
              value={totalDuration}
              color="warning.main"
              icon={<Timer fontSize="inherit" />}
            />
          </Grid>
        </Grid>
      )}

      {/* ── Error alert ── */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* ── Operations table ── */}
      {loadingOps ? (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['#', 'Work Order', 'Product', 'Scheduled Start', 'Scheduled End', 'Duration (min)', 'Status'].map(
                  (h) => (
                    <TableCell key={h}>{h}</TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 7 }).map((__, c) => (
                    <TableCell key={c}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : hasLoaded ? (
        operations.length === 0 ? (
          <Alert severity="info">
            No operations scheduled for this work center on this date.
          </Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Work Order</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Scheduled Start</TableCell>
                  <TableCell>Scheduled End</TableCell>
                  <TableCell align="right">Duration (min)</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {operations.map((op, index) => {
                  const { color, sx } = statusChipProps(op.status);
                  return (
                    <TableRow key={op.segmentId ?? op.operationId ?? index} hover>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium" color="primary">
                          {op.workOrderId ?? '--'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {op.productName ?? '--'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatTime(op.scheduledStart)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatTime(op.scheduledEnd)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {op.plannedDurationMinutes ?? '--'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={(op.status ?? 'pending').toUpperCase()}
                          color={color}
                          size="small"
                          sx={sx}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : (
        <Alert severity="info" sx={{ mt: 2 }}>
          Select a work center and date, then click <strong>Load</strong> to view the dispatch list.
        </Alert>
      )}

      {/* ── Snackbar ── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DispatchList;
